import { createRoot } from '@wordpress/element';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useState, useEffect, useRef, Fragment, useCallback } from '@wordpress/element';

// ============================================================================
// Validation Utils
// ============================================================================

const validators = {
	email: (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) ? '' : 'Invalid email',
	name: (val) => val.trim().length >= 2 ? '' : 'Name required',
	phone: (val) => val && val.replace(/\D/g, '').length >= 10 ? '' : 'Phone number required',
	address: (val) => val.trim().length >= 3 ? '' : 'Address required',
	city: (val) => val.trim().length >= 2 ? '' : 'City required',
	state: (val) => val.trim().length >= 2 ? '' : 'State required',
	zip: (val) => val.trim().length >= 3 ? '' : 'Postal code required',
	country: (val) => val ? '' : 'Country required',
};

function formatPrice(amount, currency = 'USD') {
	return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);
}

// Provinces/states picked from a list, so tax (keyed by code) always matches
const REGIONS = {
	CA: [
		['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'], ['NB', 'New Brunswick'],
		['NL', 'Newfoundland and Labrador'], ['NS', 'Nova Scotia'], ['NT', 'Northwest Territories'],
		['NU', 'Nunavut'], ['ON', 'Ontario'], ['PE', 'Prince Edward Island'], ['QC', 'Quebec'],
		['SK', 'Saskatchewan'], ['YT', 'Yukon'],
	],
	US: [
		['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'],
		['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'],
		['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'],
		['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'],
		['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
		['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
		['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
		['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'],
		['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'],
		['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'],
		['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
	],
};

// Map a saved/autofilled/typed value ("ontario", "Québec", "qc") to its code; '' if unknown
function normalizeRegion(country, value) {
	const list = REGIONS[country];
	if (!list || !value) return value || '';
	const key = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z]/g, '');
	const v = key(value);
	const match = list.find(([code, name]) => code === v || key(name) === v);
	return match ? match[0] : '';
}

// ============================================================================
// Load Google Places Script
// ============================================================================

function useGooglePlaces(apiKey) {
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		if (!apiKey) { setLoaded(false); return; }
		if (window.google?.maps?.places) { setLoaded(true); return; }
		if (window.__googlePlacesLoading) {
			window.__googlePlacesLoadedCallbacks = window.__googlePlacesLoadedCallbacks || [];
			window.__googlePlacesLoadedCallbacks.push(() => setLoaded(true));
			return;
		}
		window.__googlePlacesLoading = true;
		const script = document.createElement('script');
		script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
		script.async = true;
		script.onload = () => {
			setLoaded(true);
			if (window.__googlePlacesLoadedCallbacks) {
				window.__googlePlacesLoadedCallbacks.forEach(cb => cb());
				delete window.__googlePlacesLoadedCallbacks;
			}
		};
		script.onerror = () => setLoaded(false);
		document.head.appendChild(script);
	}, [apiKey]);

	return loaded;
}

// ============================================================================
// Input Component with Address Autocomplete
// ============================================================================

function Input({ label, id, type = 'text', value, onChange, error, required, placeholder, autoComplete, isAddress, country, onAddressSelect, googleApiKey }) {
	const inputRef = useRef(null);
	const autocompleteRef = useRef(null);
	const googleLoaded = useGooglePlaces(googleApiKey);

	useEffect(() => {
		if (!isAddress || !['US', 'CA'].includes(country) || !googleLoaded || !inputRef.current) {
			if (autocompleteRef.current && window.google?.maps?.event) {
				window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
				autocompleteRef.current = null;
			}
			return;
		}
		// Guard: Places library may not be ready yet even after script loads
		if (!window.google?.maps?.places?.Autocomplete) return;
		if (autocompleteRef.current) {
			window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
			autocompleteRef.current = null;
		}
		const options = {
			componentRestrictions: { country: country.toLowerCase() },
			fields: ['formatted_address', 'address_components'],
			types: ['address'],
		};
		autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, options);
		autocompleteRef.current.addListener('place_changed', () => {
			const place = autocompleteRef.current.getPlace();
			if (!place.address_components) return;
			const ad = {};
			place.address_components.forEach(c => {
				const t = c.types;
				if (t.includes('street_number')) ad.number = c.long_name;
				if (t.includes('route')) ad.street = c.long_name;
				if (t.includes('locality')) ad.city = c.long_name;
				if (t.includes('administrative_area_level_1')) ad.state = c.short_name;
				if (t.includes('postal_code')) ad.zip = c.long_name;
			});
			if (onAddressSelect) {
				onAddressSelect({
					address: `${ad.number || ''} ${ad.street || ''}`.trim(),
					city: ad.city || '', state: ad.state || '', zip: ad.zip || '',
				});
			}
		});
	}, [googleLoaded, isAddress, country]);

	return (
		<div className="form-group">
			<input
				ref={inputRef} id={id} type={type}
				className={`form-input${error ? ' is-invalid' : ''}`}
				value={value} onChange={(e) => onChange(e.target.value)}
				placeholder={placeholder || label} autoComplete={autoComplete}
			/>
			<label className="form-label" htmlFor={id}>{label} {required && <span style={{color:'#D42B41'}}>*</span>}</label>
			{error && <div style={{fontSize:'13px',color:'#D42B41',marginTop:'4px'}}>{error}</div>}
		</div>
	);
}

// ============================================================================
// Step Indicator
// ============================================================================

function StepIndicator({ step }) {
	const steps = [
		{ num: 1, label: 'Contact' },
		{ num: 2, label: 'Address' },
		{ num: 3, label: 'Payment' },
	];

	const isDone = (num) => step > num;
	const isActive = (num) => step === num;

	return (
		<div className="checkout-stepper">
			{steps.map((s, idx) => (
				<Fragment key={s.num}>
					<div className={`checkout-step${isActive(s.num) ? ' active' : ''}${isDone(s.num) ? ' completed' : ''}`}>
						<span className="checkout-step-circle">{s.num}</span>
						<span className="checkout-step-label">{s.label}</span>
					</div>
					{idx < steps.length - 1 && (
						<div className={`checkout-step-line${isDone(s.num) ? ' completed' : ''}`} />
					)}
				</Fragment>
			))}
		</div>
	);
}

// ============================================================================
// Sidebar — Multi-Product Cart + Order Summary
// ============================================================================

function Sidebar({ cartItems, setCartItems, totals, currency, data, setData, isMulti, hasPhysical, country, shippingRates, isSubscription, restUrl, nonce, step }) {
	const [couponInput, setCouponInput] = useState(data.couponCode || '');
	const [showCoupon, setShowCoupon] = useState(!!data.couponCode);
	const [couponStatus, setCouponStatus] = useState(''); // 'success' | 'error' | ''
	const [couponMsg, setCouponMsg] = useState('');
	const [couponLoading, setCouponLoading] = useState(false);

	const applyCoupon = async () => {
		const code = couponInput.trim();
		if (!code) return;
		setCouponLoading(true);
		// Shared with the Address step, which holds "Continue to Payment" until this settles
		setData(prev => ({ ...prev, couponPending: true }));
		setCouponStatus('');
		setCouponMsg('');
		try {
			const items = cartItems.map(i => ({ product_id: i.product_id, quantity: i.qty, variation_id: i.variation_id || '' }));
			const res = await fetch(`${restUrl}checkout/validate-coupon`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': nonce },
				body: JSON.stringify({ code }),
			});
			const json = await res.json();
			if (json.valid) {
				setCouponStatus('success');
				setCouponMsg(json.message || 'Coupon applied!');
				setData(prev => ({ ...prev, couponCode: code }));
			} else {
				setCouponStatus('error');
				setCouponMsg(json.message || 'Invalid coupon code');
			}
		} catch {
			setCouponStatus('error');
			setCouponMsg('Could not validate coupon');
		} finally {
			setCouponLoading(false);
			setData(prev => ({ ...prev, couponPending: false }));
		}
	};

	// Determine shipping zone from country
	const EU = ['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','GB','NO','CH'];
	const getZone = (c) => {
		if (c === 'CA') return 'canada';
		if (c === 'US') return 'us';
		if (EU.includes(c)) return 'europe';
		return 'international';
	};
	const zone = getZone(country);
	const zoneConfig = shippingRates[zone] || {};
	const thresholdUsd = parseFloat(zoneConfig.free_threshold) || 0;
	// Convert to cents in display currency
	const cadRate = parseFloat(shippingRates._cad_rate) || 1.40;
	const freeShippingThreshold = thresholdUsd > 0
		? Math.round(thresholdUsd * (currency === 'CAD' ? cadRate : 1) * 100)
		: 0;

	// Physical subtotal for shipping progress
	const physicalSubtotal = cartItems
		.filter((i) => i.type === 'physical')
		.reduce((sum, i) => sum + i.price * i.qty, 0);
	const subtotal = cartItems.reduce((sum, i) => sum + i.price * i.qty, 0);
	const previewDiscount = isSubscription ? Math.round(subtotal * 0.10) : 0;

	const amountNeeded = freeShippingThreshold > 0 ? Math.max(0, freeShippingThreshold - physicalSubtotal) : -1;
	const shippingProgress = freeShippingThreshold > 0 ? Math.min(100, (physicalSubtotal / freeShippingThreshold) * 100) : 0;
	const qualifiesFreeShipping = freeShippingThreshold > 0 && physicalSubtotal >= freeShippingThreshold;
	const totalQty = cartItems.reduce((sum, i) => sum + i.qty, 0);

	const itemKey = (item) => `${item.product_id}_${item.variation_id || ''}`;

	const updateItemQty = (item, newQty) => {
		const key = itemKey(item);
		setCartItems((prev) => {
			let next;
			if (newQty <= 0) {
				next = prev.filter((i) => itemKey(i) !== key);
			} else {
				next = prev.map((i) => (itemKey(i) === key ? { ...i, qty: Math.min(10, newQty) } : i));
			}
			try { localStorage.setItem('iae_cart', JSON.stringify(next)); } catch {}
			window.dispatchEvent(new CustomEvent('iae:cart-updated', { detail: { items: next, _fromCheckout: true } }));
			return next;
		});
	};

	const removeItem = (item) => {
		// GTM: push remove_from_cart event
		if (window.dataLayer) {
			const priceDollars = item.price / 100;
			window.dataLayer.push({ ecommerce: null });
			window.dataLayer.push({
				event: 'remove_from_cart',
				ecommerce: {
					currency,
					value: priceDollars * item.qty,
					items: [{
						item_id: String(item.product_id),
						item_name: item.name,
						item_variant: item.variation || undefined,
						price: priceDollars,
						quantity: item.qty,
					}],
				},
			});
		}
		const key = itemKey(item);
		setCartItems((prev) => {
			const next = prev.filter((i) => itemKey(i) !== key);
			// Sync to localStorage so cart.js and badge stay in sync
			try { localStorage.setItem('iae_cart', JSON.stringify(next)); } catch {}
			window.dispatchEvent(new CustomEvent('iae:cart-updated', { detail: { items: next, _fromCheckout: true } }));
			return next;
		});
	};

	// Currency / country mismatch notice
	let currencyNotice = null;
	if (step !== 3 && typeof window !== 'undefined' && typeof window.IAECurrencyToggle === 'function') {
		if (country === 'CA' && currency === 'USD') {
			currencyNotice = { message: 'Shipping to Canada with prices in USD.', action: 'Switch to CAD' };
		} else if (country && country !== 'CA' && currency === 'CAD') {
			currencyNotice = { message: 'Shipping outside Canada with prices in CAD.', action: 'Switch to USD' };
		}
	}

	return (
		<div className="sticky-top" style={{ top: '1.25rem' }}>
			<div className="checkout-sidebar-card">
				{currencyNotice && (
					<div className="alert alert-warning py-2 px-3 m-3 mb-0 small text-center d-flex flex-column align-items-center gap-1">
						<span>{currencyNotice.message}</span>
						<button
							type="button"
							className="checkout-coupon-toggle fw-semibold"
							onClick={() => window.IAECurrencyToggle()}
						>
							{currencyNotice.action}
						</button>
					</div>
				)}
				{/* Cart items */}
				<div className="p-4 pb-3">
					<div className="checkout-summary-title">Order Summary</div>
					{cartItems.map((item) => (
						<div key={itemKey(item)} className="checkout-item mb-3" style={{position:'relative'}}>
							<div className="d-flex" style={{gap:'16px',alignItems:'flex-start'}}>
								{item.thumbnail && (
									<div className="checkout-item-img"><img src={item.thumbnail} alt={item.name} /></div>
								)}
								<div className="checkout-item-details">
									<span className="checkout-item-name">
										{item.url ? <a href={item.url} style={{color:'inherit',textDecoration:'none'}}>{item.name}</a> : item.name}
									</span>
									{item.variation && <span className="checkout-item-format">{item.variation}</span>}
									<span className="checkout-item-price">{formatPrice(item.price, currency)} each</span>
								</div>
								<button type="button" className="checkout-item-remove" onClick={() => removeItem(item)} title="Remove" disabled={step >= 3}>&times;</button>
							</div>
							<div className="checkout-qty d-flex align-center justify-between" style={{marginTop:'12px'}}>
								<div className="d-flex align-center" style={{gap:'8px'}}>
									<button type="button" className="checkout-qty-btn" onClick={() => updateItemQty(item, item.qty - 1)} disabled={item.qty <= 1 || step >= 3}>&minus;</button>
									<span className="checkout-qty-value">{item.qty}</span>
									<button type="button" className="checkout-qty-btn" onClick={() => updateItemQty(item, item.qty + 1)} disabled={item.qty >= 10 || step >= 3}>+</button>
								</div>
								<span className="checkout-item-total">{formatPrice(item.price * item.qty, currency)}</span>
							</div>
						</div>
					))}
				</div>

				{/* Free shipping progress */}
				{hasPhysical && country && freeShippingThreshold > 0 && (
					<div className="px-4 pb-3">
						{amountNeeded > 0 ? (
							<>
								<div className="checkout-sidebar-text" style={{fontSize:'14px',marginBottom:'8px'}}>Add {formatPrice(amountNeeded, currency)} more for free shipping</div>
								<div className="checkout-shipping-progress">
									<div className="checkout-shipping-progress-bar" style={{ width: `${shippingProgress}%` }} />
								</div>
							</>
						) : (
							<div className="checkout-sidebar-highlight small fw-semibold">You qualify for free shipping</div>
						)}
					</div>
				)}

				<hr className="mx-4 my-0 checkout-sidebar-divider" />

				{/* Order details */}
				<div className="p-4 pt-3 pb-3">
					<div className="checkout-summary-row">
						<span className="checkout-sidebar-text">Subtotal ({totalQty} {totalQty === 1 ? 'item' : 'items'})</span>
						<span className="checkout-sidebar-text">{formatPrice(subtotal, currency)}</span>
					</div>

					{hasPhysical && qualifiesFreeShipping && (
						<div className="checkout-summary-row">
							<span className="checkout-sidebar-text">Shipping</span>
							<span className="checkout-sidebar-highlight fw-semibold">Free</span>
						</div>
					)}

					{hasPhysical && !qualifiesFreeShipping && totals.shipping > 0 && (
						<div className="checkout-summary-row">
							<span className="checkout-sidebar-text">Shipping</span>
							<span className="checkout-sidebar-text">{formatPrice(totals.shipping, currency)}</span>
						</div>
					)}

					{hasPhysical && !qualifiesFreeShipping && totals.shipping === 0 && (
						<div className="checkout-summary-row">
							<span className="checkout-sidebar-text">Shipping</span>
							<span className="checkout-sidebar-muted small">Calculated at checkout</span>
						</div>
					)}

					{(totals.discount > 0 || previewDiscount > 0) && (
						<div className="checkout-summary-row">
							<span className="checkout-sidebar-text">{isSubscription ? 'Auto-refill (-10%)' : 'Discount'}</span>
							<span className="checkout-sidebar-highlight fw-semibold">-{formatPrice(totals.discount || previewDiscount, currency)}</span>
						</div>
					)}

					{totals.tax_lines && totals.tax_lines.length > 0 && totals.tax_lines.filter(t => t.amount > 0).map((tax, idx) => (
						<div key={idx} className="checkout-summary-row">
							<span className="checkout-sidebar-muted">{tax.label} ({tax.rate}%)</span>
							<span className="checkout-sidebar-text">{formatPrice(tax.amount, currency)}</span>
						</div>
					))}

					{country === 'CA' && (!totals.tax_lines || totals.tax_lines.length === 0) && (
						<div className="checkout-summary-row">
							<span className="checkout-sidebar-text">Tax</span>
							<span className="checkout-sidebar-muted small">Calculated at checkout</span>
						</div>
					)}

					<div className="checkout-summary-total">
						<span className="checkout-sidebar-text">Total</span>
						<span className="checkout-sidebar-text">{formatPrice(
							(totals.discount > 0 ? totals.total : totals.total - previewDiscount)
							- (qualifiesFreeShipping && totals.shipping > 0 ? totals.shipping : 0),
							currency
						)}</span>
					</div>
				</div>

				<hr className="mx-4 my-0 checkout-sidebar-divider" />

				{/* Coupon */}
				<div className="p-4 pt-3 pb-3">
					{!showCoupon ? (
						<button type="button" className="checkout-coupon-toggle" onClick={() => setShowCoupon(true)}>Have a coupon code?</button>
					) : (
						<div className="checkout-coupon open">
							<div className="checkout-coupon-row">
								<div className="form-group" style={{marginBottom:0}}>
									<input type="text" className="checkout-coupon-input" placeholder="Coupon code" value={couponInput} onChange={(e) => setCouponInput(e.target.value)} disabled={step >= 3} />
								</div>
								{/* The payment amount is fixed once on the Payment step — a coupon there wouldn't be charged */}
								<button type="button" className="checkout-coupon-apply" onClick={applyCoupon} disabled={couponLoading || step >= 3}>{couponLoading ? "..." : "Apply"}</button>
							</div>
							{couponMsg && <div style={{fontSize:'13px',marginTop:'8px',color: couponStatus === 'success' ? '#3A8C42' : '#D42B41'}}>{couponMsg}</div>}
							{step >= 3 && <div className="text-muted" style={{fontSize:'13px',marginTop:'8px'}}>To add or change a coupon, go back to the Address step.</div>}
						</div>
					)}
				</div>

				{/* Footer trust line */}
				<div className="checkout-sidebar-footer text-center p-3">
					<small className="checkout-sidebar-muted">
						<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#C9A96E" viewBox="0 0 16 16" className="me-1" style={{ verticalAlign: '-2px' }}><path d="M5.338 1.59a61 61 0 0 0-2.837.856.48.48 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.7 10.7 0 0 0 2.287 2.233c.346.244.652.42.893.533q.18.085.293.118a1 1 0 0 0 .101.025 1 1 0 0 0 .1-.025q.114-.034.294-.118c.24-.113.547-.29.893-.533a10.7 10.7 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067s-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.8 11.8 0 0 1-2.517 2.453 7 7 0 0 1-1.048.625 2.5 2.5 0 0 1-.444.182l-.094.03a1.2 1.2 0 0 1-.292 0l-.094-.03a2.5 2.5 0 0 1-.444-.182 7 7 0 0 1-1.048-.625 11.8 11.8 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 63 63 0 0 1 5.072.56"/><path d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0"/></svg>
						Secure checkout &middot; Powered by Stripe
					</small>
				</div>
			</div>
		</div>
	);
}

// ============================================================================
// Step 1: Contact Info
// ============================================================================

function StepContact({ data, setData, errors, setErrors, onNext, loading, isPhysical }) {
	const handleChange = (field, value) => {
		setData(prev => ({ ...prev, [field]: value }));
		if (validators[field]) setErrors(prev => ({ ...prev, [field]: validators[field](value) }));
	};

	const isValid = data.email && data.name && data.phone && !validators.email(data.email) && !validators.name(data.name) && !validators.phone(data.phone);

	return (
		<div>
			<h2 className="checkout-heading">Contact <em>Information</em></h2>
			<Input label="Full Name" id="name" value={data.name} onChange={(v) => handleChange('name', v)} error={errors.name} required placeholder="John Doe" autoComplete="name" />
			<Input label="Email" id="email" type="email" value={data.email} onChange={(v) => handleChange('email', v)} error={errors.email} required placeholder="john@example.com" autoComplete="email" />
			<Input label="Phone" id="phone" type="tel" value={data.phone} onChange={(v) => handleChange('phone', v)} error={errors.phone} required placeholder="+1 (555) 000-0000" autoComplete="tel" />

			<div className="checkout-checkbox" style={{marginTop:'16px',display:'flex',alignItems:'center'}}>
				<input type="checkbox" className="form-checkbox" id="newsletter" checked={data.newsletter} onChange={(e) => setData(prev => ({ ...prev, newsletter: e.target.checked }))} />
				<span>Get our weekly journaling email every Friday, plus occasional offers. Unsubscribe anytime.</span>
			</div>

			<div className="checkout-actions mt-4">
				<button type="button" className="btn-primary" onClick={onNext} disabled={!isValid || loading}>
					{loading ? 'Processing...' : 'Continue \u2192'}
				</button>
			</div>
		</div>
	);
}

// ============================================================================
// Step 2: Billing & Shipping Address (Combined)
// ============================================================================

function StepBillingAndShipping({ data, setData, errors, setErrors, onNext, onBack, loading, isPhysical, countries, googleApiKey, subscriptionOk, subscriptionInterval }) {
	const handleChange = (field, value) => {
		if (field === 'billingCountry' || field === 'shippingCountry') {
			const prefix = field.replace('Country', '');
			setData(prev => ({ ...prev, [field]: value, [`${prefix}Address`]: '', [`${prefix}City`]: '', [`${prefix}State`]: '', [`${prefix}Zip`]: '' }));
			setErrors(prev => ({ ...prev, [`${prefix}Address`]: '', [`${prefix}City`]: '', [`${prefix}State`]: '', [`${prefix}Zip`]: '' }));
		} else if (field === 'shippingDifferent') {
			setData(prev => ({
				...prev,
				shippingDifferent: value,
				...(!value && { shippingAddress: prev.billingAddress, shippingCity: prev.billingCity, shippingState: prev.billingState, shippingZip: prev.billingZip, shippingCountry: prev.billingCountry }),
			}));
		} else {
			setData(prev => ({ ...prev, [field]: value }));
			const validatorKey = field.replace(/^(billing|shipping)/, '').replace(/^[A-Z]/, c => c.toLowerCase());
			if (validators[validatorKey]) setErrors(prev => ({ ...prev, [field]: validators[validatorKey](value) }));
		}
	};

	// Saved form data or autofill may hold a full name or a stale region — map it to a listed code
	useEffect(() => {
		['billing', 'shipping'].forEach((prefix) => {
			const current = data[`${prefix}State`];
			const code = normalizeRegion(data[`${prefix}Country`], current);
			if (code !== current) setData(prev => ({ ...prev, [`${prefix}State`]: code }));
		});
	}, [data.billingCountry, data.billingState, data.shippingCountry, data.shippingState]);

	const handleAddressSelect = (prefix, addressData) => {
		setData(prev => ({ ...prev, [`${prefix}Address`]: addressData.address, [`${prefix}City`]: addressData.city, [`${prefix}State`]: addressData.state, [`${prefix}Zip`]: addressData.zip }));
	};

	const billingValid = data.billingAddress && data.billingCity && data.billingState && data.billingZip && data.billingCountry;
	// Shipping fields only count when shown — a saved "ship elsewhere" from an
	// earlier physical order must not block a digital checkout
	const shippingValid = !isPhysical || !data.shippingDifferent || (data.shippingAddress && data.shippingCity && data.shippingState && data.shippingZip && data.shippingCountry);
	const isValid = billingValid && shippingValid;

	const addressFields = (prefix, idPrefix) => (
		<>
			<Input label="Street Address" id={`${idPrefix}-address`} value={data[`${prefix}Address`]} onChange={(v) => handleChange(`${prefix}Address`, v)} error={errors[`${prefix}Address`]} required placeholder="123 Main St" autoComplete="address-line1" isAddress={true} country={data[`${prefix}Country`]} onAddressSelect={(addr) => handleAddressSelect(prefix, addr)} googleApiKey={googleApiKey} />
			<Input label="City" id={`${idPrefix}-city`} value={data[`${prefix}City`]} onChange={(v) => handleChange(`${prefix}City`, v)} error={errors[`${prefix}City`]} required placeholder="New York" autoComplete="address-level2" />
			<div className="grid" style={{gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
				<div>
					{REGIONS[data[`${prefix}Country`]] ? (
						<div className="form-group">
							<select id={`${idPrefix}-state`} className={`form-input form-select${errors[`${prefix}State`] ? ' is-invalid' : ''}`} value={data[`${prefix}State`]} onChange={(e) => handleChange(`${prefix}State`, e.target.value)} autoComplete="address-level1">
								<option value="">{data[`${prefix}Country`] === 'CA' ? 'Select province' : 'Select state'}</option>
								{REGIONS[data[`${prefix}Country`]].map(([code, name]) => <option key={code} value={code}>{name}</option>)}
							</select>
							<label className="form-label" htmlFor={`${idPrefix}-state`}>{data[`${prefix}Country`] === 'CA' ? 'Province' : 'State'} <span style={{color:'#D42B41'}}>*</span></label>
						</div>
					) : (
						<Input label="State / Province" id={`${idPrefix}-state`} value={data[`${prefix}State`]} onChange={(v) => handleChange(`${prefix}State`, v)} error={errors[`${prefix}State`]} required placeholder="NY" autoComplete="address-level1" />
					)}
				</div>
				<div>
					<Input label="Postal Code" id={`${idPrefix}-zip`} value={data[`${prefix}Zip`]} onChange={(v) => handleChange(`${prefix}Zip`, v)} error={errors[`${prefix}Zip`]} required placeholder="10001" autoComplete="postal-code" />
				</div>
			</div>
		</>
	);

	const countrySelect = (id, field, value) => (
		<div className="form-group">
			<select id={id} className={`form-input form-select${errors[field] ? ' is-invalid' : ''}`} value={value} onChange={(e) => handleChange(field, e.target.value)}>
				<option value="">Select country</option>
				{countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
			</select>
			<label className="form-label" htmlFor={id}>Country <span style={{color:'#D42B41'}}>*</span></label>
		</div>
	);

	return (
		<div>
			<h2 className="checkout-heading">Billing <em>Address</em></h2>
			{countrySelect('billing-country', 'billingCountry', data.billingCountry)}
			{addressFields('billing', 'billing')}

			{isPhysical && (
				<>
					<label className="d-flex align-center gap-8 my-4 py-3 px-4 border rounded" htmlFor="shipping-same" style={{cursor:'pointer'}}>
						<input type="checkbox" className="form-checkbox" id="shipping-same" checked={!data.shippingDifferent} onChange={(e) => handleChange('shippingDifferent', !e.target.checked)} />
						<span style={{fontSize:'14px',fontWeight:600}}>Use same address for shipping</span>
					</label>

					{data.shippingDifferent && (
						<>
							<h2 className="checkout-heading">Shipping <em>Address</em></h2>
							{countrySelect('shipping-country', 'shippingCountry', data.shippingCountry)}
							{addressFields('shipping', 'shipping')}
						</>
					)}
				</>
			)}

			{subscriptionOk && (
				<div className="my-4 p-3 border rounded" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
					<label className="d-flex align-start gap-8" htmlFor="subscribe" style={{cursor:'pointer'}}>
						<input type="checkbox" className="form-checkbox" id="subscribe" checked={data.isSubscription} onChange={(e) => setData(prev => ({ ...prev, isSubscription: e.target.checked }))} style={{marginTop:'3px'}} />
						<span>
							<strong>Auto-refill &amp; save 10%</strong>
							<span className="text-muted" style={{display:'block',fontSize:'14px',marginTop:'4px'}}>Delivered every {subscriptionInterval || 7} months. Cancel anytime.</span>
						</span>
					</label>
				</div>
			)}

			<div className="checkout-actions mt-4">
				{/* Wait for a coupon still being checked, or the order would go out without it */}
				<button type="button" className="btn-primary" onClick={onNext} disabled={!isValid || loading || data.couponPending}>
					{loading ? 'Processing...' : 'Continue to Payment \u2192'}
				</button>
				<button type="button" className="checkout-back" onClick={onBack} disabled={loading}>&larr; Back</button>
			</div>
		</div>
	);
}

// ============================================================================
// Payment Step
// ============================================================================

function StepPayment({ totals, onBack, onPay, loading, currency, thankYouUrl, stripeMode, isSubscription }) {
	const stripe = useStripe();
	const elements = useElements();
	const [paymentError, setPaymentError] = useState('');
	const [termsAccepted, setTermsAccepted] = useState(false);

	const handlePayment = async (e) => {
		e.preventDefault();
		if (!stripe || !elements || !termsAccepted) return;
		setPaymentError('');

		// GTM: push add_payment_info when user submits payment
		if (window.dataLayer) {
			window.dataLayer.push({ ecommerce: null });
			window.dataLayer.push({
				event: 'add_payment_info',
				ecommerce: {
					currency: currency || 'USD',
					value: totals.total / 100,
					payment_type: 'stripe',
				},
			});
		}

		onPay(async () => {
			const { error } = await stripe.confirmPayment({
				elements,
				confirmParams: { return_url: thankYouUrl },
			});
			if (error) {
				setPaymentError(error.type === 'card_error' || error.type === 'validation_error' ? error.message : 'An unexpected error occurred. Please try again.');
			}
		});
	};

	return (
		<div>
			<h2 className="checkout-heading">Payment <em>Details</em></h2>

			{stripeMode === 'test' && (
				<div className="alert alert-warning py-2 px-3 mb-3 small">
					<strong>Test mode</strong>: Use card <code>4242 4242 4242 4242</code>, any future expiry, any CVC.
				</div>
			)}

			<form onSubmit={handlePayment}>
				<div className="border rounded p-4 mb-4 bg-light" style={{ minHeight: '200px' }}>
					<PaymentElement />
				</div>

				{paymentError && (
					<div className="alert alert-danger mb-3">
						<div><strong>Payment failed</strong></div>
						<div>{paymentError}</div>
					</div>
				)}

				<label className="d-flex align-center gap-8 mb-3" htmlFor="terms" style={{cursor:'pointer'}}>
					<input type="checkbox" className="form-checkbox" id="terms" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
					<span style={{fontSize:'14px'}}>I agree to the <a href="/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-red" style={{textDecoration:'underline'}}>Privacy Policy</a> and <a href="/refund_returns/" target="_blank" rel="noopener noreferrer" className="text-red" style={{textDecoration:'underline'}}>Refund & Returns Policy</a></span>
				</label>

				<div className="checkout-actions mt-4">
				<button type="submit" className="btn-primary" disabled={loading || !stripe || !elements || !termsAccepted}>
					{loading ? <><span className="spinner-border spinner-border-sm me-2" />Processing...</> : `${isSubscription ? 'Start Auto-Refill' : 'Pay'} ${formatPrice(totals.total, currency)}`}
				</button>
					<button type="button" className="checkout-back" onClick={onBack} disabled={loading}>&larr; Back</button>
				</div>
			</form>
		</div>
	);
}

// ============================================================================
// Empty Cart State
// ============================================================================

function EmptyCart() {
	return (
		<section className="section bg-cream text-center" style={{minHeight:'60vh',display:'flex',alignItems:'center'}}>
			<div className="container-narrow" style={{width:'100%'}}>
				<svg xmlns="http://www.w3.org/2000/svg" height="48" viewBox="0 -960 960 960" width="48" fill="#9C9889"><path d="M280-80q-33 0-56.5-23.5T200-160q0-33 23.5-56.5T280-240q33 0 56.5 23.5T360-160q0 33-23.5 56.5T280-80ZM680-80q-33 0-56.5-23.5T600-160q0-33 23.5-56.5T680-240q33 0 56.5 23.5T760-160q0 33-23.5 56.5T680-80ZM246-720l96 200h280l110-200H246Zm-38-80h590q23 0 35 20.5t1 41.5L692-482q-11 20-29.5 31T622-440H324l-44 80h480v80H280q-45 0-68-39.5t-2-78.5l54-98-144-304H40v-80h130l38 80Zm134 280h280-280Z"/></svg>
				<h2 className="heading mt-3">Your cart is empty</h2>
				<p className="pillars-intro mt-2">Add some products before checking out.</p>
				<a href="/shop/" className="btn-primary mt-3" style={{display:'inline-flex'}}>Browse Products</a>
			</div>
		</section>
	);
}

// ============================================================================
// Main Checkout App
// ============================================================================

const STORAGE_KEY = 'iae_checkout_customer';
const CART_STORAGE_KEY = 'iae_cart';
const SAVED_FIELDS = ['name', 'email', 'phone', 'billingAddress', 'billingCity', 'billingState', 'billingZip', 'billingCountry', 'shippingAddress', 'shippingCity', 'shippingState', 'shippingZip', 'shippingCountry', 'shippingDifferent'];

function getSavedCustomer() {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		return raw ? JSON.parse(raw) : {};
	} catch { return {}; }
}

function saveCustomer(data) {
	try {
		const toSave = {};
		SAVED_FIELDS.forEach(f => { if (data[f]) toSave[f] = data[f]; });
		localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
	} catch { /* silently fail */ }
}

function getCartItems() {
	try {
		return JSON.parse(localStorage.getItem(CART_STORAGE_KEY)) || [];
	} catch { return []; }
}

// Last unpaid order this tab created — sent back on the next "Continue to Payment"
// so the server cancels it instead of leaving it holding stock and a coupon use
const ATTEMPT_STORAGE_KEY = 'iae_checkout_attempt';

function getCheckoutAttempt() {
	try {
		return JSON.parse(sessionStorage.getItem(ATTEMPT_STORAGE_KEY)) || {};
	} catch { return {}; }
}

function saveCheckoutAttempt(attempt) {
	try {
		sessionStorage.setItem(ATTEMPT_STORAGE_KEY, JSON.stringify(attempt));
	} catch { /* silently fail */ }
}

const COUNTRIES = [
	{ code: 'US', name: 'United States' },
	{ code: 'CA', name: 'Canada' },
	{ code: 'GB', name: 'United Kingdom' },
	{ code: 'AU', name: 'Australia' },
	{ code: 'AT', name: 'Austria' },
	{ code: 'BE', name: 'Belgium' },
	{ code: 'CZ', name: 'Czech Republic' },
	{ code: 'DK', name: 'Denmark' },
	{ code: 'FI', name: 'Finland' },
	{ code: 'FR', name: 'France' },
	{ code: 'DE', name: 'Germany' },
	{ code: 'IE', name: 'Ireland' },
	{ code: 'IT', name: 'Italy' },
	{ code: 'NL', name: 'Netherlands' },
	{ code: 'PL', name: 'Poland' },
	{ code: 'ES', name: 'Spain' },
	{ code: 'SE', name: 'Sweden' },
];

function CheckoutApp({ initialData }) {
	// Track currency reactively so toggle updates formatting
	const [currency, setCurrency] = useState(initialData.currency);

	// Determine cart items: from localStorage cart, or from single product data-attributes
	const [cartItems, setCartItems] = useState(() => {
		const cart = getCartItems();
		if (cart.length > 0) {
			return cart;
		}
		// Fallback: single product mode (backwards compat via ?product_id=X)
		if (initialData.mode === 'single' && initialData.productId) {
			const item = {
				product_id: initialData.productId,
				qty: 1,
				name: initialData.productName,
				price: initialData.productPrice,
				type: initialData.productType,
				thumbnail: initialData.productThumbnail || '',
			};
			if (initialData.variationId) {
				item.variation_id = initialData.variationId;
				item.variation = initialData.variationName || initialData.variationId;
			}
			return [item];
		}
		return [];
	});

	const isMulti = cartItems.length > 1;
	const hasPhysical = cartItems.some((i) => i.type === 'physical');

	const [subscriptionInfo, setSubscriptionInfo] = useState(() => {
		// Check cart item first, then initialData
		if (!isMulti && cartItems.length === 1) {
			if (cartItems[0].subscription_ok === '1') {
				return { ok: true, interval: cartItems[0].subscription_interval || 7 };
			}
			if (initialData.productSubscriptionOk === '1') {
				return { ok: true, interval: initialData.productSubscriptionInterval || 7 };
			}
		}
		return { ok: false, interval: 7 };
	});

	// Fetch subscription info from API for cart items that don't have it
	useEffect(() => {
		if (isMulti || cartItems.length !== 1 || subscriptionInfo.ok) return;
		const productId = cartItems[0].product_id;
		if (!productId) return;
		fetch(`${initialData.restUrl}products/${productId}`)
			.then(r => r.ok ? r.json() : null)
			.then(product => {
				if (!product || !product.subscription_ok) return;
				setSubscriptionInfo({ ok: true, interval: product.subscription_interval || 7 });
			})
			.catch(() => {});
	}, []);

	const subscriptionOk = subscriptionInfo.ok;
	const subscriptionInterval = subscriptionInfo.interval;

	const [step, setStep] = useState(1);
	const [loading, setLoading] = useState(false);
	const [globalError, setGlobalError] = useState('');
	const [clientSecret, setClientSecret] = useState('');
	const [stripePromise, setStripePromise] = useState(null);
	const [honeypot, setHoneypot] = useState('');
	const formLoadedAt = useRef(Date.now());

	// Listen for cart updates from currency sync (cart.js syncCartCurrency / toggle)
	useEffect(() => {
		const handler = (e) => {
			// At payment step (3), the order + PaymentIntent are already locked — ignore toggle
			if (step === 3) return;
			// Ignore events dispatched by our own checkout updates
			if (e.detail?._fromCheckout) return;
			const fresh = e.detail?.items;
			if (fresh && fresh.length > 0) {
				setCartItems(fresh);
			}
			if (window.iaeCurrency) {
				setCurrency(window.iaeCurrency);
			}
		};
		window.addEventListener('iae:cart-updated', handler);
		return () => window.removeEventListener('iae:cart-updated', handler);
	}, [step]);

	// Compute subtotal from cart items
	const computeSubtotal = useCallback(
		() => cartItems.reduce((sum, i) => sum + i.price * i.qty, 0),
		[cartItems]
	);

	const [totals, setTotals] = useState(() => {
		const sub = cartItems.reduce((sum, i) => sum + i.price * i.qty, 0);
		return { subtotal: sub, shipping: 0, discount: 0, tax_lines: [], tax_total: 0, total: sub };
	});

	const [data, setData] = useState(() => {
		const saved = getSavedCustomer();
		const prefill = initialData.userPrefill || {};
		// Only use non-empty prefill values (don't overwrite localStorage with empty strings)
		const filteredPrefill = {};
		Object.entries(prefill).forEach(([k, v]) => { if (v) filteredPrefill[k] = v; });
		return {
			name: '', email: '', phone: '',
			billingAddress: '', billingCity: '', billingState: '', billingZip: '', billingCountry: '',
			shippingAddress: '', shippingCity: '', shippingState: '', shippingZip: '', shippingCountry: '',
			shippingDifferent: false,
			isSubscription: false, newsletter: false, couponCode: '',
			...saved,
			// User profile overrides localStorage for identity + address fields
			...filteredPrefill,
			// URL coupon overrides localStorage
			...(initialData.coupon ? { couponCode: initialData.coupon } : {}),
		};
	});

	const [errors, setErrors] = useState({});
	const cartPingSent = useRef(false);

	useEffect(() => {
		if (initialData.stripeKey) loadStripe(initialData.stripeKey).then(setStripePromise);
	}, [initialData.stripeKey]);

	// Persist customer info to localStorage
	useEffect(() => {
		saveCustomer(data);
	}, [data]);

	// Recalculate subtotal when cart items change
	useEffect(() => {
		const sub = computeSubtotal();
		setTotals((prev) => ({ ...prev, subtotal: sub, total: sub - prev.discount + prev.shipping }));
	}, [cartItems]);

	// GTM: push begin_checkout on first render when cart has items
	useEffect(() => {
		if (cartItems.length === 0 || !window.dataLayer) return;
		const cur = currency || 'USD';
		const gtmItems = cartItems.map((item, idx) => ({
			item_id: String(item.product_id),
			item_name: item.name,
			item_variant: item.variation || undefined,
			price: item.price / 100,
			quantity: item.qty,
			index: idx,
		}));
		const value = cartItems.reduce((sum, i) => sum + (i.price / 100) * i.qty, 0);
		window.dataLayer.push({ ecommerce: null });
		window.dataLayer.push({
			event: 'begin_checkout',
			ecommerce: { currency: cur, value, items: gtmItems },
		});
	}, []);

	// Empty cart state
	if (cartItems.length === 0) {
		return <EmptyCart />;
	}

	const goToPayment = async () => {
		// A coupon still being checked would be left off this order
		if (data.couponPending) return;
		// Anti-bot checks
		if (honeypot) return;
		const elapsed = (Date.now() - formLoadedAt.current) / 1000;
		if (elapsed < 3) { setGlobalError('Please slow down and try again.'); return; }

		setLoading(true);
		setGlobalError('');
		try {
			// Subscription only for single-product mode
			const isSubscription = !isMulti && data.isSubscription;
			const endpoint = isSubscription ? 'checkout/subscribe' : 'checkout';
			const shipCountry = data.shippingDifferent ? data.shippingCountry : data.billingCountry;
			const shipAddr = data.shippingDifferent ? data.shippingAddress : data.billingAddress;
			const shipCity = data.shippingDifferent ? data.shippingCity : data.billingCity;
			const shipState = data.shippingDifferent ? data.shippingState : data.billingState;
			const shipZip = data.shippingDifferent ? data.shippingZip : data.billingZip;

			// Build request body
			const effectiveCountry = hasPhysical ? shipCountry : data.billingCountry;
			const effectiveProvince = hasPhysical ? shipState : data.billingState;
			const body = {
				email: data.email, name: data.name, phone: data.phone,
				country: effectiveCountry,
				province: effectiveProvince,
				billing_address: { line1: data.billingAddress, city: data.billingCity, state: data.billingState, postal_code: data.billingZip, country: data.billingCountry },
				...(hasPhysical && {
					shipping_address: { line1: shipAddr, city: shipCity, state: shipState, postal_code: shipZip, country: shipCountry },
				}),
				coupon_code: data.couponCode,
				newsletter_consent: data.newsletter,
				_hp: honeypot,
				_ts: Math.floor(elapsed),
				// Charge in the currency shown here, even if the header toggle changed the cookie
				currency,
			};

			const attempt = getCheckoutAttempt();
			if (attempt.order_id && attempt.token) {
				body.replace_order_id = attempt.order_id;
				body.replace_token = attempt.token;
			}

			// Send items array (works for both single and multi)
			if (isSubscription && cartItems.length === 1) {
				// Subscription endpoint expects single product_id + qty
				body.product_id = cartItems[0].product_id;
				body.qty = cartItems[0].qty;
				if (cartItems[0].variation) body.variation = cartItems[0].variation;
				if (cartItems[0].variation_id) body.variation_id = cartItems[0].variation_id;
			} else {
				body.items = cartItems.map((i) => ({ product_id: i.product_id, qty: i.qty, variation: i.variation || '', variation_id: i.variation_id || '' }));
			}

			const response = await fetch(`${initialData.restUrl}${endpoint}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': initialData.nonce },
				body: JSON.stringify(body),
			});

			const resp = await response.json();
			if (resp.code === 'previous_attempt_paid') {
				// The earlier attempt was paid or is still processing — show that order
				// instead of charging again. The saved attempt is kept on purpose: while
				// the payment is processing it is what blocks a second one. The
				// confirmed-paid thank-you page clears it (cart.js).
				if (resp.redirect) {
					window.location.href = resp.redirect;
					return;
				}
			}
			if (!response.ok) throw new Error(resp.message || 'Something went wrong. Please try again.');
			// Server returns totals in dollars — convert to cents for formatPrice()
			setTotals({
				subtotal: Math.round(resp.totals.subtotal * 100),
				shipping: Math.round(resp.totals.shipping * 100),
				discount: Math.round(resp.totals.discount * 100),
				tax_lines: (resp.totals.tax_lines || []).map(t => ({
					label: t.label,
					rate: t.rate,
					amount: Math.round(t.amount * 100),
				})),
				tax_total: Math.round((resp.totals.tax_total || 0) * 100),
				total: Math.round(resp.totals.total * 100),
			});
			// Show the Payment step in the currency the server actually charges
			if (['USD', 'CAD'].includes(resp.totals.currency)) setCurrency(resp.totals.currency);
			setClientSecret(resp.client_secret);
			saveCheckoutAttempt({ order_id: resp.order_id, token: resp.checkout_token });

			// GTM: push add_shipping_info when address step completes
			if (window.dataLayer && cartItems.length > 0) {
				const cur = currency || 'USD';
				const gtmItems = cartItems.map((item, idx) => ({
					item_id: String(item.product_id),
					item_name: item.name,
					item_variant: item.variation || undefined,
					price: item.price / 100,
					quantity: item.qty,
					index: idx,
				}));
				const value = cartItems.reduce((sum, i) => sum + (i.price / 100) * i.qty, 0);
				window.dataLayer.push({ ecommerce: null });
				window.dataLayer.push({
					event: 'add_shipping_info',
					ecommerce: { currency: cur, value, items: gtmItems },
				});
			}

			setStep(3);
		} catch (err) {
			setGlobalError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const handlePayment = async (confirmFn) => {
		setLoading(true);
		try { await confirmFn(); } catch (err) { setGlobalError(err.message); } finally { setLoading(false); }
	};

	return (
		<section className="checkout section bg-white">
		<div className="checkout-container container">
			{/* Honeypot — hidden from users, bots auto-fill it */}
			<div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
				<input type="text" name="website_url" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
			</div>

			{/* Step Indicator */}
			<StepIndicator step={step} />

			{/* Two Column Layout */}
			<div className="grid checkout-grid">
				{/* Left Column: Form */}
				<div className="col-7 col-md-12">
					{globalError && <div className="alert alert-danger mb-3">{globalError}</div>}

					{step === 1 && <StepContact data={data} setData={setData} errors={errors} setErrors={setErrors} onNext={() => {
						// `begin_checkout` already fires on initial cart render
						// (see effect above); not re-pushed here to avoid inflating
						// the funnel step count.
						setStep(2);
						// Fire cart ping for abandoned cart tracking (fire-and-forget)
						if (!cartPingSent.current && data.email) {
							cartPingSent.current = true;
							try {
								const params = new URLSearchParams({
									iam_cart_ping: '1',
									email: data.email,
									cart_key: Date.now().toString(36),
									consent_cart: data.newsletter ? '1' : '0',
									cart_json: JSON.stringify(cartItems.map(i => ({ product_id: i.product_id, name: i.name, qty: i.qty, price: i.price, variation: i.variation || '' }))),
								});
								fetch(`/?${params.toString()}`, { method: 'GET', keepalive: true }).catch(() => {});
							} catch (e) { /* silent */ }
						}
					}} loading={loading} isPhysical={hasPhysical} />}
					{step === 2 && <StepBillingAndShipping data={data} setData={setData} errors={errors} setErrors={setErrors} onNext={goToPayment} onBack={() => setStep(1)} loading={loading} isPhysical={hasPhysical} countries={COUNTRIES} googleApiKey={initialData.googleApiKey} subscriptionOk={subscriptionOk} subscriptionInterval={subscriptionInterval} />}
					{step === 3 && stripePromise && clientSecret && (
						<Elements stripe={stripePromise} options={{ clientSecret }}>
							<StepPayment totals={totals} onBack={() => setStep(2)} onPay={handlePayment} loading={loading} currency={currency} thankYouUrl={initialData.thankYouUrl} stripeMode={initialData.stripeMode} isSubscription={subscriptionOk && data.isSubscription} />
						</Elements>
					)}
				</div>

				{/* Right Column: Sidebar */}
				<div className="col-5 col-md-12">
					<Sidebar
						cartItems={cartItems}
						setCartItems={setCartItems}
						totals={totals}
						currency={currency}
						data={data}
						setData={setData}
						isMulti={isMulti}
						hasPhysical={hasPhysical}
						country={data.shippingDifferent ? data.shippingCountry : data.billingCountry}
						shippingRates={initialData.shippingRates}
						isSubscription={subscriptionOk && data.isSubscription}
						restUrl={initialData.restUrl}
						nonce={initialData.nonce}
						step={step}
					/>
				</div>
			</div>
		</div>
		</section>
	);
}

// ============================================================================
// Mount
// ============================================================================

document.addEventListener('DOMContentLoaded', () => {
	const container = document.querySelector('.wp-block-wpl-checkout-form');
	if (!container) return;

	const d = container.dataset;
	const mode = d.mode || 'single';

	const initialData = {
		mode,
		productId: d.productId ? parseInt(d.productId, 10) : 0,
		productName: d.productName || '',
		productType: d.productType || 'physical',
		productPrice: d.productPrice ? parseInt(d.productPrice, 10) : 0,
		productThumbnail: d.productThumbnail || '',
		productSubscriptionOk: d.productSubscriptionOk || '0',
		productSubscriptionInterval: d.productSubscriptionInterval ? parseInt(d.productSubscriptionInterval, 10) : 7,
		variationId: d.variationId || '',
		variationName: d.variationName || '',
		coupon: d.coupon || '',
		stripeKey: d.stripeKey,
		restUrl: d.restUrl,
		nonce: d.nonce,
		thankYouUrl: d.thankYouUrl,
		currency: d.currency,
		googleApiKey: d.googleApiKey || '',
		stripeMode: d.stripeMode || 'test',
		shippingRates: d.shippingRates ? JSON.parse(d.shippingRates) : {},
		// Logged-in user profile for pre-fill
		userPrefill: d.userName ? {
			name: d.userName,
			email: d.userEmail || '',
			phone: d.userPhone || '',
			billingAddress: d.userBillingAddress || '',
			billingCity: d.userBillingCity || '',
			billingState: d.userBillingState || '',
			billingZip: d.userBillingZip || '',
			billingCountry: d.userBillingCountry || '',
			shippingAddress: d.userShippingAddress || '',
			shippingCity: d.userShippingCity || '',
			shippingState: d.userShippingState || '',
			shippingZip: d.userShippingZip || '',
			shippingCountry: d.userShippingCountry || '',
		} : null,
	};
	container.innerHTML = '';
	createRoot(container).render(<CheckoutApp initialData={initialData} />);
});
